import numeral from 'numeral';
import { Money } from '@app/currency';
import type { LegacyProduct } from '@app/types';
import isNil from 'lodash/isNil';
import { TFunction } from 'i18next';
import type { DateFormatter, TimeFormatter } from '@app/datetime';

// When fetched via GQL, "traits" property under Product given productTraits alias as to not clash with "traits" property within Product.Variants
export interface IronProduct extends LegacyProduct {
  productTraits?: Array<{ name: string; value: string }>;
  product?: {
    traits: Array<{ name: string; value: string }>;
  };
}

type ProductDisplayDataProps = (
  product: IronProduct,
  type: string,
  t: TFunction,
  formatDate: DateFormatter,
  formatTime: TimeFormatter,
  currency: string,
  browsePageLabel?: string,
) => { label: string; amount?: string } | undefined;

const getProductTrait = (product, traitName: string) => {
  // Base products have alias 'productTraits' for traits array
  if (!isNil(product?.productTraits)) {
    return product?.productTraits?.find((trait) => trait.name === traitName)
      ?.value;
  }

  // Otherwise fetch variant product traits array using 'traits'
  return product?.traits?.find((trait) => trait.name === traitName)?.value;
};

const getProductDisplayData: ProductDisplayDataProps = (
  product,
  type,
  t,
  formatDate,
  formatTime,
  currency,
  browsePageLabel = '',
) => {
  switch (type) {
    case 'average_deadstock_price':
      return {
        label: browsePageLabel,
        amount: Money.getDollars(product.market?.averageDeadstockPrice, true),
      };
    case 'below_retail_and_trending_deal':
    case 'below_retail': {
      const retailPrice = getProductTrait(product, 'Retail Price');

      const lowestAsk =
        (!isNil(product.market.bidAskData) &&
          product.market.bidAskData.lowestAsk) ||
        product.market.lowestAsk;

      return {
        label: t('below_retail_amount', {
          amount: retailPrice - lowestAsk,
        }),
      };
    }

    case 'deadstock_sold':
      return {
        label: formatSoldLabel(
          t,
          product.market?.statistics?.annual?.salesCount ||
            product.market?.deadstockSold,
        ),
      };
    case 'last_sale':
      return {
        label: t('pdp_last_sale_label'),
        amount: Money.getDollars(
          product.market?.statistics?.lastSale?.amount ||
            product.market?.salesInformation?.lastSale ||
            product.market?.lastSale,
          true,
          true,
          currency,
        ),
      };
    case 'price_premium':
      return {
        label: browsePageLabel,
        amount: product.market.pricePremium
          ? numeral(product.market.pricePremium).format('0%')
          : '--',
      };
    case 'recent_asks': {
      const { lastLowestAskTime } = product.market;
      return {
        label: formatTime(lastLowestAskTime, 'date_and_time', true),
      };
    }

    case 'recent_bids': {
      const { lastHighestBidTime } = product.market;
      return {
        label: formatTime(lastHighestBidTime, 'date_and_time', true),
      };
    }

    case 'release_date':
      return {
        label: browsePageLabel,
        amount: formatDate(
          getProductTrait(product, 'Release Date'),
          'short_date_alt',
        ),
      };
    case 'sales_this_period':
      return {
        label: formatSoldLabel(
          t,
          product.market?.statistics?.last72Hours?.salesCount ||
            product.market?.salesThisPeriod,
        ),
      };
    case 'volatility':
      return {
        label: browsePageLabel,
        amount: product.market.volatility
          ? numeral(product.market.volatility).format('0%')
          : '--',
      };
    case 'your_ask':
      return {
        label: t('Your Ask:'),
      };
    case 'your_bid':
      return {
        label: t('Your Bid:'),
      };
    default:
      return undefined; // should error out
  }
};

const formatSoldLabel = (t, sold: number) => {
  const label = sold > 1 ? t('sold_plural.other') : t('sold_plural.one');
  return sold ? `${sold} ${label}` : '--';
};

export default getProductDisplayData;
